import type { PartnerBillingProfileResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { Grid2, Typography } from "@mui/material";
import dayjs from "dayjs";

type PartnerBillingProfileInfoWidgetProps = {
	billingProfile: PartnerBillingProfileResponseDto;
	index: number;
};

const billingProfileResource = Resources.base.partner.detail.billingProfile;

export function PartnerBillingProfileInfoWidget({
	billingProfile,
	index,
}: PartnerBillingProfileInfoWidgetProps) {
	const { t } = useResource();
	return (
		<BaseCard title={`${t(billingProfileResource.title)} #${index + 1}`}>
			<Grid2 container>
				<Grid2 size={{ xs: 6 }}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(billingProfileResource.vatId)}
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{billingProfile.vatId}
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(billingProfileResource.businessId)}
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{billingProfile.businessId}
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(billingProfileResource.vatLiable)}
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{billingProfile.vatLiable
							? `${t(billingProfileResource.vatLiableTrue)}`
							: `${t(billingProfileResource.vatLiableFalse)}`}
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(billingProfileResource.invoicingName)}
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{billingProfile.invoicingName}
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						Type
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{billingProfile.type}
					</Typography>
				</Grid2>

				{billingProfile.type === "individual" && (
					<Grid2 container>
						<Grid2 size={{ xs: 6 }}>
							<Typography variant="body1" color="secondary" fontWeight={600}>
								Birth Country
							</Typography>
							<Typography variant="subtitle1" mb={0.5}>
								{billingProfile?.birthCountry}
							</Typography>
						</Grid2>

						<Grid2 size={{ xs: 6 }}>
							<Typography variant="body1" color="secondary" fontWeight={600}>
								Birth City
							</Typography>
							<Typography variant="subtitle1" mb={0.5}>
								{billingProfile?.birthCity}
							</Typography>
						</Grid2>

						<Grid2 size={{ xs: 6 }}>
							<Typography variant="body1" color="secondary" fontWeight={600}>
								Birth Date
							</Typography>
							<Typography variant="subtitle1" mb={0.5}>
								{billingProfile?.birthDate
									? dayjs(billingProfile.birthDate).format("DD.MM.YYYY")
									: "-"}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 6 }}>
							<Typography variant="body1" color="secondary" fontWeight={600}>
								TIN
							</Typography>
							<Typography variant="subtitle1" mb={0.5}>
								{billingProfile?.tin}
							</Typography>
						</Grid2>
					</Grid2>
				)}

				{billingProfile.address && (
					<Grid2 container>
						<Grid2 size={{ xs: 6 }}>
							<Typography variant="body1" color="secondary" fontWeight={600}>
								Street
							</Typography>
							<Typography variant="subtitle1" mb={0.5}>
								{billingProfile?.address.street}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 6 }}>
							<Typography variant="body1" color="secondary" fontWeight={600}>
								Building number
							</Typography>
							<Typography variant="subtitle1" mb={0.5}>
								{billingProfile?.address.buildingNumber}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 6 }}>
							<Typography variant="body1" color="secondary" fontWeight={600}>
								City
							</Typography>
							<Typography variant="subtitle1" mb={0.5}>
								{billingProfile?.address.city}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 6 }}>
							<Typography variant="body1" color="secondary" fontWeight={600}>
								Post code
							</Typography>
							<Typography variant="subtitle1" mb={0.5}>
								{billingProfile?.address.postCode}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 6 }}>
							<Typography variant="body1" color="secondary" fontWeight={600}>
								Country
							</Typography>
							<Typography variant="subtitle1" mb={0.5}>
								{billingProfile?.address.country}
							</Typography>
						</Grid2>
					</Grid2>
				)}
			</Grid2>
		</BaseCard>
	);
}
